import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import { Player } from '@lordicon/react';
import { Alert, Button, ButtonGroup, Container, Row, Col } from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useTranslator,
  useToast,
  useAppContext,
  callNativeAppRemoveLastRoute,
  withMobileAppMode,
  FormikInput,
  FormikSelect,
} from '@front-activerse/common-components';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import TrackerFeedback from '../components/TrackerFeedback';
import { TrackerMealAiModal } from '../components/TrackerMeal/TrackerMealModals';
import { Ingredient } from '../types';
import MacroFields from './MacroFields';
import validationSchema from './validationSchema';
import { getScannedFrontPhoto } from '../ScanLabel/TrackerScanLabelAnalyzingContainer';
import {
  AddUserProductFormValues,
  emptyFormValues,
  toMutationInput,
  scanResultToFormValues,
  addedProductToIngredient,
  addedProductToMealData,
  caloriesMismatchRatio,
  CALORIE_MISMATCH_THRESHOLD,
  resolveErrorField,
  collectErrorFieldPaths,
  SCAN_LABEL_RESULT_STORAGE_KEY,
  isScanResultConsumed,
  markScanResultConsumed,
  SCAN_LABEL_RESULT_EVENT,
} from './helpers';
import { hasAnyPaidAccess, withForcedAiError } from '../helpers';

const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';

// Where this form's "Scan label" button sends the user.
//
// `add-product` belongs in the scanner's URL only where the form really does sit underneath it —
// the add-ingredient and describe-meal flows, whose form is a child route of a meal being edited.
// This form also exists standalone at `/tracker/add-product` (and its user-meals twin), reached from
// the action sheet by way of a scan; there the form is *above* the scanner, so nesting the scanner
// under it would describe a hierarchy that does not exist. Those use the flat `/scan-label` that
// scan-plate and scan-barcode use.
// Matched exactly, not by suffix: the nested forms also end in `/add-product`
// (`/tracker/:date/meal-details/:id/add-ingredient/add-product`), and sending those to the flat path
// would drop the meal they belong to.
const STANDALONE_FORM_PATHS: Record<string, string> = {
  '/tracker/add-product': '/tracker/scan-label',
  '/meals/user-meals/add-product': '/meals/user-meals/scan-label',
};

export const scanLabelPath = (pathname: string) =>
  STANDALONE_FORM_PATHS[pathname.replace(/\/$/, '')] ?? 'scan-label';
const ADD_INGREDIENT_RETURN_STORAGE_KEY = 'tracker_add_ingredient_return_state';
const ADD_INGREDIENT_RETURN_EVENT = 'tracker_add_ingredient_return';

interface AddUserProductLocationState {
  ingredients?: Ingredient[];
  mealName?: string;
  returnPath?: string;
  servings?: number;
  stepsBack?: number;
  ean?: string;
  // Set when entering from the standalone barcode scanner with no meal in progress: after saving,
  // create a new meal containing this product and open the meal editor (instead of navigate(-1),
  // which would fall back onto the scanner). `fromUserMeals` picks the meal-editor base path.
  createMealFromProduct?: boolean;
  fromUserMeals?: boolean;
  // Set by the search screen's "Add manually" button: open a blank form and ignore any stored scan.
  manualEntry?: boolean;
}

interface TrackerAddUserProductContainerProps {
  addUserProduct: (input: ReturnType<typeof toMutationInput>) => Promise<unknown>;
  // Optional: only the autoScan flow uses it, to turn the scanned front-of-product photo into the
  // image of the meal this form creates.
  uploadImage?: (image: File, scope: string) => Promise<unknown>;
  mobileAppMode?: boolean;
}

const TrackerAddUserProductContainer: React.FC<TrackerAddUserProductContainerProps> = ({
  addUserProduct,
  uploadImage,
  mobileAppMode = false,
}) => {
  const { t } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as AddUserProductLocationState | null) ?? {};
  const { toastError, toastSuccess } = useToast();
  const {
    images: { aiBadgeTiny, scanLabelIcon },
    lotties: { infoIcon },
  } = useAppContext();

  // Picks the scan-label button's copy. In the native webview the access keys are never written (the
  // app opens the webview straight on this URL), so trust the app's own gating there rather than
  // prompting a user who has already paid to buy access.
  const canScanLabel = mobileAppMode || hasAnyPaidAccess();

  const [initialValues, setInitialValues] = useState<AddUserProductFormValues>(emptyFormValues);
  const [isAiPrefilled, setIsAiPrefilled] = useState(false);
  const [scanRequestId, setScanRequestId] = useState<string | null>(null);
  // Two-step save when stated calories drift too far from the Atwater estimate: the first submit
  // surfaces a warning and arms confirmation; the second submit (button now "Confirm and save")
  // goes through. Reset whenever the macro/mode inputs change so a fresh edit re-checks.
  const [calorieWarningArmed, setCalorieWarningArmed] = useState(false);
  // The AI signature's "i" opens the same explanation modal the meal ingredient list uses, so the
  // copy and behaviour stay identical in both places.
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  // Set when the backend refuses the save because the daily AI limit is spent. Cleared on the next
  // submit so a stale warning never sits above a retry that has not been answered yet.
  const [rateLimitHit, setRateLimitHit] = useState(false);
  const rateLimitAlertRef = useRef<HTMLDivElement>(null);
  const calorieWarningRef = useRef<HTMLDivElement>(null);
  const playerRefInfo = useRef<Player>(null);
  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();
    return color || '#000000';
  }, []);
  // Scope for the "first invalid field" lookup, so the query can't reach inputs outside this form.
  const formRef = useRef<HTMLFormElement>(null);

  const multiplier = parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');

  useEffect(() => {
    if (!calorieWarningArmed) return;
    // Scroll to the warning itself. This used to target the actions row, but that is `.sticky-footer`
    // — pinned to the bottom of the viewport and therefore always in view, so scrollIntoView on it
    // had nothing to do. The warning sits above it inside the scroll area, below the macro fields, so
    // on a phone it sat under the fold — the save looked like it had not registered and testers
    // pressed it again, missing that the button label had changed (IP-11123).
    //
    // rAF so the Alert is in the DOM before we measure it, and `center` rather than `end` so the
    // sticky footer does not cover it — the same pair the rate-limit alert uses.
    requestAnimationFrame(() => {
      // jsdom does not implement scrollIntoView.
      if (typeof calorieWarningRef.current?.scrollIntoView === 'function') {
        calorieWarningRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [calorieWarningArmed]);

  const applyScanPayload = (payload: any) => {
    if (
      payload?.primaryIngredient &&
      typeof payload.primaryIngredient === 'object' &&
      typeof payload.primaryIngredient.name === 'string'
    ) {
      setInitialValues((current) =>
        scanResultToFormValues(payload.primaryIngredient, payload.formValues ?? current)
      );
      setIsAiPrefilled(true);
      if (typeof payload.requestId === 'string') setScanRequestId(payload.requestId);
    }
  };

  // Two consumption paths, because either one can be the only one available:
  //
  // - CustomEvent, for when this form is ALREADY mounted while the scanner sits on top of it
  //   (SlideRoutes uses destroy={false}, so a mount effect would never re-run).
  // - the sessionStorage copy, for when the form mounts fresh AFTER the result was produced
  //   (the autoScan flow, "Analyze again" re-opening the form, or a reload).
  //
  // Neither may delete the stored copy on sight. A kept-mounted instance used to consume the event
  // and clear storage, so the instance that actually became visible mounted to an empty store and
  // showed a blank form. Dedupe on `updatedAt` instead: each instance applies a given result at most
  // once, and the copy simply stays until the next scan overwrites it.
  const appliedScanAtRef = useRef<number | null>(null);

  // `fromStorage` distinguishes the two sources, because "Add manually" must suppress only one of
  // them. That entry means "open a blank form", so a scan that already sat in sessionStorage before
  // the user got here (abandoned earlier, kept for the "Analyze again" round trip) must not prefill
  // it — IP-11103. But pressing "Scan label" ON this form is an explicit request for a scan, and its
  // result always arrives as the CustomEvent: that flow returns with navigate(-2) onto this very
  // history entry, which still carries manualEntry. Suppressing the event too left the user staring
  // at an empty form after a successful scan.
  const applyScanPayloadOnce = (payload: any, { fromStorage = false } = {}) => {
    if (fromStorage && locationState.manualEntry) return;
    const updatedAt = typeof payload?.updatedAt === 'number' ? payload.updatedAt : null;
    // Already applied by THIS instance, or its flow already finished (product saved). The latter is
    // what stopped a completed scan from prefilling every later visit to this form — e.g. a
    // scan-plate meal, then add-ingredient, then add-product, which arrived carrying the old values.
    if (updatedAt !== null && appliedScanAtRef.current === updatedAt) return;
    if (isScanResultConsumed(updatedAt)) return;
    appliedScanAtRef.current = updatedAt;
    applyScanPayload(payload);
  };

  useEffect(() => {
    const handleScanResult = (event: Event) => {
      applyScanPayloadOnce((event as CustomEvent).detail);
    };
    window.addEventListener(SCAN_LABEL_RESULT_EVENT, handleScanResult);
    return () => window.removeEventListener(SCAN_LABEL_RESULT_EVENT, handleScanResult);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem(SCAN_LABEL_RESULT_STORAGE_KEY);
    if (raw) {
      try {
        applyScanPayloadOnce(JSON.parse(raw), { fromStorage: true });
      } catch {
        // ignore malformed payloads
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any edit to macros or a mode switch invalidates a pending calorie-mismatch confirmation.
  const resetCalorieWarning = () => {
    if (calorieWarningArmed) setCalorieWarningArmed(false);
  };

  const goBackToMeal = () => {
    const stepsBack = locationState.stepsBack ?? 1;
    for (let i = 0; i < stepsBack; i += 1) {
      callNativeAppRemoveLastRoute();
    }
    navigate(-stepsBack);
  };

  // Cancel abandons the whole flow, so it must clear whatever that flow pushed to get here — not
  // just the single entry `navigate(-1)` assumes.
  //
  // Only the scan-label autoScan path (action sheet -> scanner -> analysis) *pushes* this form on
  // top of two live entries, so one step back lands on the analysis screen the user already left
  // rather than on the tracker:
  //
  //   scan label (autoScan):  tracker -> scan-label -> analyzing -> [form]  => 3
  //   barcode:                tracker -> scan-barcode -> [form]            => 1
  //   form-first scan:        returns with navigate(-2), so both scanner entries are gone => 1
  //   no scan at all:         only the form was pushed => 1
  //
  // The barcode entry sets `createMealFromProduct` too but carries an `ean` and pushes over a single
  // entry, so it must stay at 1 — going back 3 there would overshoot the tracker. The pending
  // scan photos cannot serve as the signal either: nothing clears them, so they are still set on the
  // form-first path, which needs 1.
  const isAutoScanEntry = Boolean(locationState.createMealFromProduct) && !locationState.ean;

  const handleCancel = () => {
    const stepsBack = isAutoScanEntry ? 3 : 1;
    for (let i = 0; i < stepsBack; i += 1) {
      callNativeAppRemoveLastRoute();
    }
    navigate(-stepsBack);
  };

  const handleSubmit = async (
    values: AddUserProductFormValues,
    {
      setFieldError,
      setFieldTouched,
      setSubmitting,
    }: {
      setFieldError: (field: string, message: string) => void;
      setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
      setSubmitting: (v: boolean) => void;
    }
  ) => {
    // A previous refusal must not linger over a retry that has not been answered yet.
    setRateLimitHit(false);
    // Atwater cross-check: if stated calories drift > threshold from the macro-derived estimate,
    // block the first submit with a warning and require an explicit second confirm.
    const mismatch = caloriesMismatchRatio(values);
    if (!calorieWarningArmed && mismatch != null && mismatch > CALORIE_MISMATCH_THRESHOLD) {
      setCalorieWarningArmed(true);
      setSubmitting(false);
      return;
    }

    try {
      // withForcedAiError passes the real response through untouched unless developer options
      // select a failure — this save is rate-limited like the scan calls, so its error states have
      // to be reviewable the same way.
      const result: any = withForcedAiError(
        await addUserProduct(toMutationInput(values, locationState.ean))
      );

      if (result?.__typename === 'AddedUserProduct') {
        // The scan's flow ends here, whichever branch it takes. The stored copy stays put (the
        // "Analyze again" round trip still needs to find it), but it must not prefill anything again.
        markScanResultConsumed(appliedScanAtRef.current);
        if (locationState.returnPath) {
          const ingredient = addedProductToIngredient(result, values, multiplier);
          const payload = {
            returnPath: locationState.returnPath,
            ingredients: [...(locationState.ingredients ?? []), ingredient],
            mealName: locationState.mealName ?? '',
            ...(locationState.servings != null && { servings: locationState.servings }),
            updatedAt: Date.now(),
          };
          sessionStorage.setItem(ADD_INGREDIENT_RETURN_STORAGE_KEY, JSON.stringify(payload));
          window.dispatchEvent(new CustomEvent(ADD_INGREDIENT_RETURN_EVENT, { detail: payload }));
          toastSuccess(<p>{t('tracker.meal.ingredients.addedConfirmation')}</p>);
          goBackToMeal();
        } else if (locationState.createMealFromProduct) {
          // Entered from the standalone barcode scanner with no meal in progress: build a brand-new
          // meal containing just this product and open the meal editor, mirroring the found-product
          // path. navigate(-1) here would land back on the scanner.
          const ingredient = addedProductToIngredient(result, values, multiplier);
          const dietMealId = Math.floor(Math.random() * 1000000000);
          // Scan-label entry only: the front-of-product shot becomes the new meal's image. The
          // barcode entry into this same branch has no photo, so `getScannedFrontPhoto()` is null
          // there and the meal keeps its empty image, exactly as before.
          const frontPhoto = getScannedFrontPhoto();
          let mealImageUrl = '';
          if (frontPhoto && uploadImage) {
            try {
              const upload = (await uploadImage(frontPhoto, 'meal')) as {
                __typename?: string;
                imageUrl?: string;
              };
              if (upload?.__typename === 'UploadImageSuccess' && upload.imageUrl) {
                mealImageUrl = upload.imageUrl;
              }
            } catch {
              // A failed upload must not block saving the product — the meal just opens without an
              // image, which the user can still set from the meal editor.
            }
          }
          const mealData = {
            ...addedProductToMealData(ingredient, dietMealId),
            ...(mealImageUrl && { preparationImageUrl: mealImageUrl }),
          };
          // How far back the meal editor must go to reach the tracker, since it navigates with
          // `-tracker_depth`. `replace: true` below means the meal editor takes this form's own
          // entry, so what is left underneath depends on how the user got here:
          //
          //   barcode:    tracker -> scan-barcode -> [meal-details]                    => 2
          //   scan label: tracker -> scan-label -> analyzing -> [meal-details]          => 3
          //
          // Using 2 for the scan-label flow landed the user back on the analysis screen, which
          // bounced them to the camera instead of the tracker.
          sessionStorage.setItem('tracker_depth', frontPhoto ? '3' : '2');
          const basePath = locationState.fromUserMeals
            ? '/meals/user-meals/describe-meal/meal-details'
            : '/tracker/describe-meal/meal-details';
          navigate(`${basePath}/${dietMealId}`, {
            state: { mealData, timestamp: Date.now() },
            replace: true,
          });
        } else {
          toastSuccess(<p>{t('tracker.meal.ingredients.addedConfirmation')}</p>);
          callNativeAppRemoveLastRoute();
          navigate(-1);
        }
      } else if (result?.__typename === 'RateLimitExceeded') {
        // The daily AI limit now covers saving a product too, not just scanning (the backend stopped
        // exempting addUserProduct from the limiter). Without this branch the union member fell
        // through to the generic `toastError()` below, which told the user nothing about why.
        //
        // Shown as an in-content Alert rather than a toast, matching the scan screens: a toast
        // disappears, and here the user is left looking at a form they just filled in with no
        // explanation of why Save did nothing. The values stay put — the limit resets, so retrying
        // later must not mean re-typing everything.
        setRateLimitHit(true);
        scrollToRateLimitAlert();
      } else if (result?.__typename === 'ValidationException') {
        const erroredFields: string[] = [];
        (result.details ?? []).forEach((detail: { fieldName: string; messages: string[] }) => {
          const field = resolveErrorField(detail.fieldName, values.mode);
          if (field && detail.messages?.length) {
            erroredFields.push(field);
            // FormikInput only renders an error once the field is touched; a server-side
            // ValidationException can land on a field the user never blurred, so mark it
            // touched (without re-triggering client-side validation) alongside the error.
            setFieldTouched(field, true, false);
            setFieldError(field, detail.messages.join(' '));
          }
        });
        // A server-side rejection can land on a field that is scrolled off screen (the form is
        // taller than the viewport on mobile), so take the user to it as well.
        scrollToFirstError(erroredFields);
        if (!result.details?.length && !result.messages?.length) {
          toastError();
        }
        // Non-field-level messages (e.g. general validation failures not tied to a specific
        // input) must never be silently dropped, even when field-level details were also present.
        if (result.messages?.length) {
          toastError(<p>{result.messages.join(' ')}</p>);
        }
      } else {
        toastError();
      }
    } catch {
      toastError();
    } finally {
      setSubmitting(false);
    }
  };

  const unitSuffix = (unit: AddUserProductFormValues['unit']) =>
    unit === 'MILLILITERS' ? 'ml' : 'g';

  /**
   * Scrolls to the topmost field carrying an error and focuses it.
   *
   * Order comes from the DOM, not a hardcoded field list: the visual order is what "first invalid
   * field" means to the user, and it stays correct when the form is reordered or when the mode swap
   * changes which macro set is mounted. Runs after a paint so the inputs Formik just marked invalid
   * have rendered.
   */
  // The Alert renders below the macro fields, which on mobile sits under the fold — without this the
  // save just appears to do nothing.
  const scrollToRateLimitAlert = () => {
    requestAnimationFrame(() => {
      // jsdom does not implement scrollIntoView.
      if (typeof rateLimitAlertRef.current?.scrollIntoView === 'function') {
        rateLimitAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  const scrollToFirstError = (fieldNames: string[]) => {
    if (!fieldNames.length) return;
    requestAnimationFrame(() => {
      const form = formRef.current;
      if (!form) return;
      // Field paths are dotted ("per100.calories"), which is legal inside an attribute selector's
      // quoted value, so no escaping is needed beyond the quotes.
      const selector = fieldNames.map((n) => `[name="${n}"]`).join(',');
      const first = form.querySelector<HTMLElement>(selector);
      if (!first) return;
      // Guard scrollIntoView: jsdom (test env) doesn't implement it.
      if (typeof first.scrollIntoView === 'function') {
        // preventScroll on focus below: scrollIntoView owns the scrolling here, and letting focus
        // scroll too fights the smooth animation and lands off-centre.
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      first.focus({ preventScroll: true });
    });
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.addProduct.navbarTitle')}</TitleBar>

      <div className="page-template-tracker edit-meal-page">
        {/* Formik wraps the Container rather than sitting inside a Col: the sticky footer has to be a
            sibling of the Container to span the full page width (see TrackerAddEditMeal, which does
            the same), and it still needs Formik's state for the Save button. */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnMount
        >
          {({ values, setFieldValue, setFieldTouched, isSubmitting, dirty, validateForm }) => {
            // Editing a macro field: clear any pending calorie-mismatch confirmation AND
            // mark the field touched so a format/limit error shows immediately, instead of
            // only after the input loses focus (Formik touches on blur by default).
            const onMacroChange = (fieldPath: string) => {
              resetCalorieWarning();
              // Mark touched so a format/limit error shows immediately (Formik touches on
              // blur by default). shouldValidate=false: Formik already re-validates on the
              // value change; passing true here would validate against the pre-update value
              // (setValue is batched) and wrongly keep the field in an error state.
              setFieldTouched(fieldPath, true, false);
            };
            // Switching mode: clear the calorie warning and reset the touched state of the
            // mode we're switching TO — but ONLY for fields that are still empty. An empty
            // required field shouldn't flash red just because the user visited it before,
            // yet a field the user actually filled with an invalid value must KEEP its error
            // across mode round-trips (100g → serving → 100g). So we only untouch blanks.
            const switchMode = (mode: AddUserProductFormValues['mode']) => {
              setFieldValue('mode', mode);
              resetCalorieWarning();
              const group = mode === 'PER_SERVING' ? 'perServing' : 'per100';
              const targetMacros = mode === 'PER_SERVING' ? values.perServing : values.per100;
              (['calories', 'protein', 'fat', 'carbs', 'fiber'] as const).forEach((f) => {
                if (targetMacros[f].trim() === '') setFieldTouched(`${group}.${f}`, false, false);
              });
              if (mode === 'PER_SERVING' && values.servingSize.trim() === '')
                setFieldTouched('servingSize', false, false);
            };
            return (
              <Form noValidate data-testid="add-product-form" ref={formRef}>
                <Container className="py-md-4 full-height">
                  <Row>
                    <Col xl={{ size: 10, offset: 1 }}>
                      {/* `.page-container` carries the white surface from `md` up, where
                                `.edit-meal-page` hands the page background back to the beige brand
                                variant. Without it this form was the one tracker screen showing that
                                variant behind its fields — DescribeMeal, ScanPlateDetails,
                                TrackerAddEditMeal and the rest all wrap their content this way. */}
                      <div className="page-container">
                        <div className="tracker-container">
                          <h2 className="title-small semi-bold mb-m">
                            {t('addIngredient.productDetails.title')}
                          </h2>

                          {/* autoComplete off on both: a product name and a food brand are nothing the
                            browser can know, but a field literally called `name` sitting next to
                            `brand` reads to Chrome as a contact form, so it offered saved address and
                            company cards over the field. An unknown token ("do-not-autofill") did
                            NOT work — Chrome leaves `autocomplete` unparsed and falls back to those
                            same name-based heuristics; a recognised value is what it honours. */}
                          <FormikInput
                            name="name"
                            label={t('addIngredient.productName')}
                            translateErrMsg
                            autoComplete="off"
                            data-testid="add-product-name"
                            data-test-id="add_product_name"
                          />
                          <FormikInput
                            name="brand"
                            label={t('addIngredient.brandName')}
                            translateErrMsg
                            autoComplete="off"
                            data-testid="add-product-brand"
                            data-test-id="add_product_brand"
                          />

                          {/* Label scanning needs a paid access (any of diet/workout/AI — the same
                            condition ScanLabelResolver enforces). The button always navigates to
                            the scanner route, which shows the access screen when the user has none:
                            that keeps the form on the history stack, so the back button returns to
                            it with the typed-in values intact. */}
                          {/* d-flex + centring, rather than the default inline-block: the icon has
                            vertical-align:middle and lands dead centre, but the label sits on the
                            baseline, and Onest's asymmetric metrics (16px ascent / 5px descent
                            against a 20px line-height) push the glyphs ~1px above the button's
                            centre. Flex aligns both children on the cross axis instead. Scoped to
                            this button on purpose — the same drift affects icon+text buttons
                            app-wide, which is a design-system fix rather than one for this flow. */}
                          <Button
                            type="button"
                            color="secondary"
                            outline
                            // Small rather than default: the no-access label is long enough to wrap to
                            // two lines (Figma 9905:57406), which the default size cannot hold.
                            size="sm"
                            className="w-100 mb-l d-flex align-items-center justify-content-center"
                            onClick={() =>
                              navigate(scanLabelPath(location.pathname), {
                                state: { formValues: values },
                              })
                            }
                            data-testid="add-product-scan-label"
                            data-test-id="add_product_scan_label"
                          >
                            <img src={scanLabelIcon} alt="" className="me-2" />
                            {/* Three states: re-scan after a scan already filled the form, plain scan
                              with access, and the buy prompt without it. */}
                            {isAiPrefilled
                              ? t('tracker.addProduct.rescanLabel.button')
                              : canScanLabel
                              ? t('tracker.addProduct.scanLabel.button')
                              : t('tracker.addProduct.scanLabel.noAccess.button')}
                          </Button>

                          <h2 className="title-small semi-bold mb-m">
                            {t('addIngredient.nutritionValues.title')}
                          </h2>

                          <FormikSelect
                            name="unit"
                            label={t('tracker.addProduct.unit.label')}
                            translateErrMsg
                            data-testid="add-product-unit"
                            data-test-id="add_product_unit"
                          >
                            <option value="GRAMS">{t('tracker.addProduct.unit.grams')}</option>
                            <option value="MILLILITERS">
                              {t('tracker.addProduct.unit.milliliters')}
                            </option>
                          </FormikSelect>

                          <ButtonGroup className="w-100 mb-m">
                            <Button
                              type="button"
                              className="w-50"
                              color={values.mode === 'PER_100_UNITS' ? 'primary' : 'secondary'}
                              outline={values.mode !== 'PER_100_UNITS'}
                              onClick={() => switchMode('PER_100_UNITS')}
                              data-testid="add-product-mode-per-100"
                              data-test-id="add_product_mode_per_100"
                            >
                              {values.unit === 'MILLILITERS'
                                ? t('tracker.addProduct.mode.per100ml')
                                : t('tracker.addProduct.mode.per100g')}
                            </Button>
                            <Button
                              type="button"
                              className="w-50"
                              color={values.mode === 'PER_SERVING' ? 'primary' : 'secondary'}
                              outline={values.mode !== 'PER_SERVING'}
                              onClick={() => switchMode('PER_SERVING')}
                              data-testid="add-product-mode-per-serving"
                              data-test-id="add_product_mode_per_serving"
                            >
                              {t('tracker.addProduct.mode.perServing')}
                            </Button>
                          </ButtonGroup>

                          {values.mode === 'PER_SERVING' && (
                            <FormikInput
                              name="servingSize"
                              label={t('tracker.addProduct.servingSize.label')}
                              unit={unitSuffix(values.unit)}
                              inputMode="decimal"
                              maxLength={6}
                              translateErrMsg
                              autoComplete="off"
                              data-testid="add-product-serving-size"
                              data-test-id="add_product_serving_size"
                            />
                          )}

                          <MacroFields mode={values.mode} onFieldChange={onMacroChange} />

                          {calorieWarningArmed && (
                            // Wrapped so the ref lands on a plain DOM node — the same reason the
                            // rate-limit alert is wrapped.
                            <div ref={calorieWarningRef}>
                              <Alert
                                color="warning"
                                fade={false}
                                className="mt-l"
                                data-testid="add-product-calorie-warning"
                                data-test-id="add_product_calorie_warning"
                              >
                                <p>{t('tracker.addProduct.caloriesMismatch.warning')}</p>
                              </Alert>
                            </div>
                          )}

                          {rateLimitHit && (
                            <div ref={rateLimitAlertRef}>
                              <Alert
                                // danger, as on the scan screens: the save was refused, not merely
                                // questioned like the calorie mismatch above.
                                color="danger"
                                fade={false}
                                className="mt-l"
                                data-testid="add-product-rate-limit"
                                data-test-id="add_product_rate_limit"
                              >
                                <p className="mb-0">{t('tracker.limitUsageError.notification')}</p>
                              </Alert>
                            </div>
                          )}

                          {isAiPrefilled && (
                            <div className="d-flex align-items-center mt-l">
                              <img src={aiBadgeTiny} alt="" className="me-1" />
                              <p className="description text-text-variant-color mb-0">
                                {t('tracker.aiSignature')}
                              </p>
                              <Button
                                color="link"
                                type="button"
                                className="p-0 ms-s border-0 bg-transparent"
                                onClick={() => setIsAiModalOpen(true)}
                                aria-label={t('tracker.aiSignature.popup.title')}
                                onMouseEnter={() => playerRefInfo.current?.playFromBeginning()}
                                onTouchStart={() => playerRefInfo.current?.playFromBeginning()}
                                data-testid="add-product-ai-info"
                                data-test-id="add_product_ai_info"
                              >
                                <Player
                                  ref={playerRefInfo}
                                  icon={infoIcon}
                                  size={20}
                                  colorize={iconsIconMiddleColor}
                                />
                              </Button>
                            </div>
                          )}

                          {/* Rate the scan-label AI result (same thumbs component as scan-plate) */}
                          {scanRequestId && <TrackerFeedback requestId={scanRequestId} />}

                          {isAiPrefilled && (
                            <TrackerMealAiModal
                              isOpen={isAiModalOpen}
                              onToggle={() => setIsAiModalOpen((prev) => !prev)}
                              t={t}
                              // This screen analyses a product, not a meal, so its own copy.
                              descriptionKey="tracker.aiSignature.product.popup.description"
                            />
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>

                {/* Outside the Container, so the bar spans the page rather than the white card
                          it would otherwise be boxed inside — TrackerMealStickyFooter sits at this
                          same level. Still pinned to the bottom of the viewport: after a label scan
                          the form is long enough that Save/Cancel fell below the fold. */}
                <div className="sticky-footer">
                  <Container className="px-3">
                    <Row>
                      {/* Same column as the other tracker sticky footers: full width on phones,
                          narrowed to a centred third from `md` up so the actions stay under the
                          form rather than stretching across the whole page. */}
                      <Col xs="12" md={{ size: 4, offset: 4 }}>
                        <Row>
                          <Col xs="6">
                            <Button
                              type="button"
                              color="secondary"
                              outline
                              className="w-100"
                              onClick={handleCancel}
                              data-testid="add-product-cancel"
                              data-test-id="add_product_cancel"
                            >
                              {t('tracker.addProduct.cancelButton')}
                            </Button>
                          </Col>
                          <Col xs="6">
                            <Button
                              type="submit"
                              color="primary"
                              className="w-100"
                              // Enabled as soon as the user has entered anything: a Save button
                              // greyed out on arrival gives no clue what is missing. Formik still
                              // blocks the actual submit while the form is invalid — the click
                              // now reports why by jumping to the offending field.
                              //
                              // `isAiPrefilled` counts as input too. A scan fills the form via
                              // `initialValues` and `enableReinitialize`, so Formik reports
                              // `dirty: false` — the user saw a complete form with a dead Save
                              // button and had to type into some field to unlock it.
                              disabled={(!dirty && !isAiPrefilled) || isSubmitting}
                              onClick={async () => {
                                const errors = await validateForm();
                                scrollToFirstError(collectErrorFieldPaths(errors));
                              }}
                              data-testid="add-product-save"
                              data-test-id="add_product_save"
                            >
                              {calorieWarningArmed
                                ? t('tracker.addProduct.confirmSave.button')
                                : t('tracker.saveButton')}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Container>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Layout>
  );
};

export default withMobileAppMode(TrackerAddUserProductContainer);
